import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { log } from '../utils/protokoll';
import { einmalNeuLaden, istChunkLadefehler } from '../utils/chunkFehler';

/**
 * Fängt Ausnahmen aus dem Rendern ab.
 *
 * ## Warum das gebraucht wird
 *
 * Ohne Fehlergrenze reißt eine einzelne werfende Komponente den
 * **gesamten** Bildschirm mit — React hängt den Baum ab, und der
 * Benutzer sieht eine weiße Seite. Kein Menü, kein Hinweis, keine
 * Möglichkeit, woanders hinzugehen. Dass etwas in der Konsole steht,
 * hilft ihm nicht.
 *
 * Die Fehlerklasse ist hier nicht theoretisch: ein `.map()` auf einem
 * Feld, das die API nicht liefert, oder eine Zahl, die als Zeichenkette
 * ankommt, sind in diesem Projekt mehrfach vorgekommen. Beides fällt
 * beim Übersetzen nicht auf.
 *
 * ## Warum sie um den Inhalt und nicht um alles liegt
 *
 * Das Menü liegt außerhalb. Nur so bleibt der Weg auf eine andere Seite
 * offen — und genau das will jemand, dessen Seite gerade nicht geht.
 *
 * ## Warum eine Klasse
 *
 * `componentDidCatch` und `getDerivedStateFromError` gibt es nur an
 * Klassenkomponenten; React bietet dafür keinen Hook. Das ist die
 * einzige Klassenkomponente des Projekts, und sie ist es aus diesem
 * Grund.
 *
 * ## Ohne automatischen Test -- und warum
 *
 * Es gab einen E2E-Test dazu. Er sollte den Fehler echt auslösen, indem
 * er einer Listenseite statt eines Feldes ein Objekt unterschiebt. Die
 * Seite hat das anstandslos verkraftet: die Abfragen sind defensiv genug
 * geschrieben, dass daraus kein Renderfehler entsteht.
 *
 * Der Test wurde entfernt statt umgebaut. Ihn auf eine Seite zu
 * richten, die zufällig KEINE Absicherung hat, hiesse einen Test zu
 * bauen, der beim nächsten sinnvollen Zusatz an jener Seite rot wird --
 * und der dann entfernt statt verstanden würde.
 *
 * Diese Komponente ist damit nicht automatisch geprüft. Das ist eine
 * bewusste Lücke und keine übersehene: sie greift oder sie greift nicht,
 * und ob sie greift, sieht man beim ersten echten Fehler. Wer sie ändert,
 * prüft von Hand -- ein `throw` in eine Seitenkomponente schreiben,
 * aufrufen, wieder entfernen.
 *
 * ## Was hier NICHT abgefangen wird
 *
 * Fehler aus Ereignisbehandlern und aus asynchronem Code. Die laufen an
 * React vorbei — dafür sind die `onError`-Zweige der Abfragen und
 * Mutationen zuständig, und die gibt es an den betroffenen Stellen
 * bereits.
 */

interface Props {
  children: ReactNode;
  /** Wechselt der Wert, wird der Fehler vergessen — etwa bei einem Seitenwechsel. */
  zuruecksetzenBei?: unknown;
}

interface State {
  fehler: Error | null;
}

export class Fehlergrenze extends Component<Props, State> {
  state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  componentDidUpdate(vorher: Props): void {
    // Ein Seitenwechsel soll die Anwendung wieder benutzbar machen, ohne
    // dass jemand neu laden muss. Ohne das bliebe der Fehlerzustand
    // bestehen, auch wenn die neue Seite völlig in Ordnung ist.
    if (this.state.fehler && vorher.zuruecksetzenBei !== this.props.zuruecksetzenBei) {
      this.setState({ fehler: null });
    }
  }

  componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // Veralteter Chunk nach einem Deployment: neue Fassung laden, nicht melden.
    if (istChunkLadefehler(fehler) && einmalNeuLaden()) return;

    // Mit Komponentenpfad: die Fehlermeldung allein sagt oft nicht, wo es
    // passiert ist, weil der Aufrufstapel im Produktionsbau verkürzt ist.
    // log.error meldet zusätzlich an GlitchTip (siehe utils/protokoll.ts).
    log.error('Fehler beim Rendern:', fehler, { componentStack: info.componentStack });
  }

  render(): ReactNode {
    if (!this.state.fehler) return this.props.children;

    return (
      <div className="mx-auto max-w-2xl py-12">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="mb-3 flex items-center gap-2.5">
            <AlertTriangle size={22} className="text-red-600" />
            <h1 className="text-lg font-bold text-red-900">Diese Ansicht ließ sich nicht anzeigen</h1>
          </div>

          <p className="text-sm text-red-800">
            Das ist ein Fehler der Anwendung, nicht Ihrer Eingabe. Ihre gespeicherten Daten sind
            davon nicht betroffen — es ist nichts verlorengegangen, was bereits gespeichert war.
          </p>
          <p className="mt-2 text-sm text-red-800">
            Nicht gespeicherte Eingaben dieser Ansicht sind allerdings weg. Über das Menü links
            kommen Sie auf jede andere Seite.
          </p>

          {/*
            Die technische Meldung steht da, aber klein und unten. Sie
            nützt beim Melden des Fehlers; als Überschrift wäre sie für
            den Benutzer nur beunruhigend und ohne Aussage.
          */}
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-red-700">
              Technische Meldung
            </summary>
            <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-xs text-red-900">
              {this.state.fehler.name}: {this.state.fehler.message}
            </pre>
          </details>

          <button
            onClick={() => this.setState({ fehler: null })}
            className="btn btn-secondary mt-4 inline-flex items-center gap-2 text-sm"
          >
            <RefreshCw size={15} /> Erneut versuchen
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Fehlergrenze, die bei einem Seitenwechsel vergisst.
 *
 * Ohne das Zurücksetzen bliebe der Fehlertext stehen, auch wenn der
 * Benutzer längst auf einer anderen, funktionierenden Seite ist — und die
 * Anwendung wäre nur durch Neuladen wieder benutzbar.
 *
 * Getrennt von der Klasse, weil `useLocation` ein Hook ist und in einer
 * Klassenkomponente nicht aufgerufen werden kann.
 */
export function SeitenFehlergrenze({ children }: { children: ReactNode }) {
  const ort = useLocation();
  return <Fehlergrenze zuruecksetzenBei={ort.pathname}>{children}</Fehlergrenze>;
}

export default Fehlergrenze;
